import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Send, Zap, MessageSquare, TrendingUp, BarChart3, ArrowRight,
  CheckCircle, XCircle, Clock, RefreshCw, Users, CreditCard,
  ArrowUpRight, ArrowDownRight, Minus, BadgeCheck, Key, Activity,
  AlertTriangle, ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import API from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { CUSTOMER } from "@/constants/testIds";

const TOOLTIP_STYLE = {
  background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8,
  color: "#0F172A", fontSize: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.08)"
};

const STATUS_CONFIG = {
  delivered: { icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200", label: "Delivered" },
  failed:    { icon: XCircle,     color: "text-red-600",     bg: "bg-red-50 border-red-200",       label: "Failed" },
  submitted: { icon: Clock,       color: "text-[#0c1e41]",   bg: "bg-[#eef1f8] border-[#c0cfe8]", label: "Submitted" },
  queued:    { icon: Clock,       color: "text-amber-600",   bg: "bg-amber-50 border-amber-200",   label: "Queued" },
};

const DONUT_COLORS = ["#10B981", "#EF4444", "#F59E0B", "#0c1e41"];

// Small KPI card with trend
function KpiCard({ label, value, icon: Icon, iconBg, trend, trendLabel, sub, testId }) {
  const TrendIcon = trend === "up" ? ArrowUpRight : trend === "down" ? ArrowDownRight : Minus;
  const trendColor = trend === "up" ? "text-emerald-600" : trend === "down" ? "text-red-500" : "text-slate-400";
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-all duration-200 group">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center`}>
          <Icon size={18} className="text-white" />
        </div>
        {trendLabel && (
          <span className={`flex items-center gap-0.5 text-xs font-semibold ${trendColor} bg-opacity-10 px-2 py-1 rounded-full`}>
            <TrendIcon size={13} /> {trendLabel}
          </span>
        )}
      </div>
      <div className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 mb-0.5">{value}</div>
      <div className="text-xs font-medium text-slate-500">{label}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

// Quick action tile
function ActionTile({ label, icon: Icon, onClick, primary, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-2 rounded-2xl px-3 py-5 text-sm font-semibold transition-all duration-200 border ${
        primary
          ? "bg-[#0c1e41] hover:bg-[#091629] text-white border-[#0c1e41] shadow-lg shadow-[#0c1e41]/20"
          : "bg-white hover:bg-slate-50 text-slate-700 border-slate-200 hover:border-[#0c1e41]/30 hover:shadow-sm"
      }`}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${primary ? "bg-white/15" : "bg-slate-100"}`}>
        <Icon size={17} className={primary ? "text-white" : "text-slate-600"} />
      </div>
      {label}
    </button>
  );
}

// Custom donut tooltip
const DonutTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-lg text-sm">
      <p className="font-semibold text-slate-900">{payload[0].name}</p>
      <p className="text-slate-600">{payload[0].value.toLocaleString()} messages</p>
    </div>
  );
};

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [smsStats, setSmsStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [contacts, setContacts] = useState({ total: 0 });
  const [senderIds, setSenderIds] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [w, s, m, cd, cg, sid, ak] = await Promise.all([
        API.get("/wallet/balance"),
        API.get("/sms/stats"),
        API.get("/sms/messages?limit=6"),
        API.get("/sms/chart-data?days=7"),
        API.get("/contacts/groups").catch(() => ({ data: { groups: [] } })),
        API.get("/sender-ids").catch(() => ({ data: { sender_ids: [] } })),
        API.get("/api-keys").catch(() => ({ data: { api_keys: [] } })),
      ]);
      setWallet(w.data);
      setSmsStats(s.data);
      setRecentMessages(m.data.messages || []);
      setChartData(cd.data.data || []);
      const totalContacts = (cg.data.groups || []).reduce((acc, g) => acc + (g.count || 0), 0);
      setContacts({ total: totalContacts, groups: (cg.data.groups || []).length });
      setSenderIds((sid.data.sender_ids || []).filter(s => s.status === "approved"));
      setApiKeys(ak.data.api_keys || []);
    } catch { if (!silent) toast.error("Failed to load dashboard"); }
    finally { setLoading(false); setRefreshing(false); }
  };

  const balance = wallet?.balance || 0;
  const isLowBalance = balance < 50 && !loading;
  const totalSent = smsStats?.total || 0;
  const deliveryRate = smsStats?.delivery_rate || 0;

  // Donut data
  const donutData = [
    { name: "Delivered", value: smsStats?.delivered || 0 },
    { name: "Failed",    value: smsStats?.failed || 0 },
    { name: "Pending",   value: smsStats?.pending || 0 },
    { name: "Other",     value: Math.max(0, totalSent - (smsStats?.delivered || 0) - (smsStats?.failed || 0) - (smsStats?.pending || 0)) },
  ].filter(d => d.value > 0);

  const skeleton = "bg-slate-100 animate-pulse rounded-xl";

  return (
    <div data-testid={CUSTOMER.dashboard} className="space-y-6 pb-8">

      {/* ── Header ───────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-slate-900">
            Welcome back, <span className="text-[#0c1e41]">{user?.name?.split(" ")[0] || "there"}</span>
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {new Date().toLocaleDateString("en-NG", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>
        <button
          onClick={() => loadData(true)}
          className={`flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-all ${refreshing ? "opacity-50 pointer-events-none" : ""}`}
          data-testid="dashboard-refresh">
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* ── Hero + Account Health ─────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Hero Credit Card */}
        <div className="lg:col-span-7 relative overflow-hidden bg-[#0c1e41] rounded-2xl p-7 shadow-xl shadow-[#0c1e41]/20">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-white translate-y-1/3 -translate-x-1/4" />
          </div>
          <div className="relative z-10">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-white/50 mb-1">Available Credits</p>
                {loading ? (
                  <div className="w-40 h-12 bg-white/10 rounded-xl animate-pulse" />
                ) : (
                  <div data-testid={CUSTOMER.balanceCard}
                    className="text-4xl sm:text-5xl font-black tracking-tight text-white">
                    {balance.toLocaleString()}
                  </div>
                )}
              </div>
              <div className="w-12 h-12 bg-white/10 border border-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <Zap size={22} className="text-white" />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex flex-col">
                <span className="text-xs text-white/50 uppercase tracking-wider">Sent Today</span>
                <span className="text-lg font-bold text-white">{loading ? "—" : (smsStats?.today || 0).toLocaleString()}</span>
              </div>
              <div className="w-px h-8 bg-white/20" />
              <div className="flex flex-col">
                <span className="text-xs text-white/50 uppercase tracking-wider">This Month</span>
                <span className="text-lg font-bold text-white">{loading ? "—" : (smsStats?.this_month || 0).toLocaleString()}</span>
              </div>
              <div className="w-px h-8 bg-white/20" />
              <div className="flex flex-col">
                <span className="text-xs text-white/50 uppercase tracking-wider">Delivery Rate</span>
                <span className="text-lg font-bold text-white">{loading ? "—" : `${deliveryRate}%`}</span>
              </div>
            </div>
            {isLowBalance && (
              <div className="mt-5 flex items-center gap-2 bg-amber-400/20 border border-amber-400/30 rounded-xl px-4 py-2.5">
                <AlertTriangle size={14} className="text-amber-300" />
                <span className="text-xs text-amber-200 font-medium">Low balance — top up to keep sending</span>
                <button onClick={() => navigate("/dashboard/wallet/buy")}
                  className="ml-auto text-xs font-bold text-amber-300 hover:text-white flex items-center gap-1">
                  Top up <ChevronRight size={12} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Account Health */}
        <div className="lg:col-span-5 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900 mb-4">Account Health</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#eef1f8] rounded-lg flex items-center justify-center">
                  <BadgeCheck size={15} className="text-[#0c1e41]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800">Sender IDs</p>
                  <p className="text-xs text-slate-400">Approved & ready</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-sm font-bold ${senderIds.length > 0 ? "text-emerald-600" : "text-amber-600"}`}>{loading ? "—" : senderIds.length}</p>
                <p className="text-xs text-slate-400">approved</p>
              </div>
            </div>
            <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#eef1f8] rounded-lg flex items-center justify-center">
                  <Key size={15} className="text-[#0c1e41]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800">API Keys</p>
                  <p className="text-xs text-slate-400">Active integrations</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-slate-900">{loading ? "—" : apiKeys.filter(k => k.status === "active").length}</p>
                <p className="text-xs text-slate-400">active</p>
              </div>
            </div>
            <div className="flex items-center justify-between py-2.5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[#eef1f8] rounded-lg flex items-center justify-center">
                  <Users size={15} className="text-[#0c1e41]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800">Contacts</p>
                  <p className="text-xs text-slate-400">{contacts.groups || 0} group{contacts.groups !== 1 ? "s" : ""}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-slate-900">{loading ? "—" : contacts.total.toLocaleString()}</p>
                <p className="text-xs text-slate-400">total</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── KPI Cards ─────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Sent" value={loading ? "—" : totalSent.toLocaleString()}
          icon={Send} iconBg="bg-[#0c1e41]" trend="up" trendLabel={`${smsStats?.today || 0} today`} />
        <KpiCard label="Delivered" value={loading ? "—" : (smsStats?.delivered || 0).toLocaleString()}
          icon={CheckCircle} iconBg="bg-emerald-600" trend="up" trendLabel={`${deliveryRate}%`} />
        <KpiCard label="Failed / Pending" value={loading ? "—" : `${smsStats?.failed || 0} / ${smsStats?.pending || 0}`}
          icon={Activity} iconBg="bg-red-500" trend={smsStats?.failed > 0 ? "down" : "neutral"} />
        <KpiCard label="Delivery Rate" value={loading ? "—" : `${deliveryRate}%`}
          icon={TrendingUp} iconBg="bg-amber-500"
          sub={totalSent > 0 ? `${smsStats?.delivered || 0} of ${totalSent} msgs` : "Send SMS to track"} />
      </div>

      {/* ── Quick Actions ─────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <ActionTile label="Send SMS" icon={Send} primary onClick={() => navigate("/dashboard/compose")} testId="quick-send-sms" />
        <ActionTile label="Buy Credits" icon={CreditCard} onClick={() => navigate("/dashboard/wallet/buy")} testId="quick-buy-credits" />
        <ActionTile label="Contacts" icon={Users} onClick={() => navigate("/dashboard/contacts")} testId="quick-contacts" />
        <ActionTile label="Campaigns" icon={BarChart3} onClick={() => navigate("/dashboard/campaigns")} testId="quick-campaigns" />
      </div>

      {/* ── Charts Row ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* SMS Activity Area Chart */}
        <div className="lg:col-span-8 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-sm font-bold text-slate-900">SMS Activity</h3>
              <p className="text-xs text-slate-400 mt-0.5">Last 7 days — sent vs delivered</p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-[#0c1e41]" /><span className="text-slate-500">Sent</span></div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-emerald-500" /><span className="text-slate-500">Delivered</span></div>
            </div>
          </div>
          {loading ? (
            <div className="h-[200px] bg-slate-50 rounded-xl animate-pulse" />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="sentGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0c1e41" stopOpacity={0.12} />
                    <stop offset="95%" stopColor="#0c1e41" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="delivGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.12} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="day" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Area type="monotone" dataKey="sent" name="Sent" stroke="#0c1e41" fill="url(#sentGrad)" strokeWidth={2} dot={{ fill: "#0c1e41", strokeWidth: 0, r: 3 }} />
                <Area type="monotone" dataKey="delivered" name="Delivered" stroke="#10B981" fill="url(#delivGrad)" strokeWidth={2} dot={{ fill: "#10B981", strokeWidth: 0, r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Delivery Breakdown Donut */}
        <div className="lg:col-span-4 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="mb-5">
            <h3 className="text-sm font-bold text-slate-900">Delivery Breakdown</h3>
            <p className="text-xs text-slate-400 mt-0.5">All-time message status</p>
          </div>
          {loading ? (
            <div className="h-[200px] bg-slate-50 rounded-xl animate-pulse" />
          ) : donutData.length === 0 ? (
            <div className="h-[200px] flex flex-col items-center justify-center text-center">
              <MessageSquare size={32} className="text-slate-200 mb-3" />
              <p className="text-xs text-slate-400">Send your first SMS to see breakdown</p>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={donutData} cx="50%" cy="50%" innerRadius={48} outerRadius={72} paddingAngle={3} dataKey="value" startAngle={90} endAngle={-270}>
                    {donutData.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<DonutTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {donutData.map((d, i) => (
                  <div key={d.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                      <span className="text-xs text-slate-600">{d.name}</span>
                    </div>
                    <span className="text-xs font-bold text-slate-800">{d.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Recent Messages + Credit Usage ───────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Recent Messages */}
        <div className="lg:col-span-8 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-900">Recent Messages</h3>
            <button onClick={() => navigate("/dashboard/sent")} className="text-xs text-[#0c1e41] font-semibold flex items-center gap-1 hover:underline">
              View all <ArrowRight size={12} />
            </button>
          </div>
          {loading ? (
            <div className="divide-y divide-slate-100">
              {[...Array(4)].map((_, i) => <div key={i} className="h-14 mx-6 my-2 bg-slate-100 rounded-lg animate-pulse" />)}
            </div>
          ) : recentMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-6">
              <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mb-3">
                <MessageSquare size={22} className="text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-700 mb-1">No messages yet</p>
              <p className="text-xs text-slate-400 mb-4">Start sending SMS to see your history here</p>
              <Button size="sm" className="bg-[#0c1e41] hover:bg-[#091629]" onClick={() => navigate("/dashboard/compose")}>
                <Send size={13} className="mr-2" /> Send First SMS
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentMessages.map((m) => {
                const cfg = STATUS_CONFIG[m.status] || STATUS_CONFIG.submitted;
                const StatusIcon = cfg.icon;
                return (
                  <div key={m._id} className="flex items-center gap-4 px-6 py-3.5 hover:bg-slate-50/70 transition-colors">
                    <div className={`w-7 h-7 rounded-full border flex items-center justify-center shrink-0 ${cfg.bg}`}>
                      <StatusIcon size={13} className={cfg.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono font-semibold text-slate-900">{m.recipient}</span>
                        <span className="text-xs text-slate-400 font-mono bg-slate-100 px-1.5 py-0.5 rounded">{m.sender_id}</span>
                      </div>
                      <p className="text-xs text-slate-500 truncate max-w-xs mt-0.5">{m.message}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color}`}>
                        {cfg.label}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        {m.created_at ? new Date(m.created_at).toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" }) : "—"}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Credit Usage Bar Chart */}
        <div className="lg:col-span-4 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="mb-5">
            <h3 className="text-sm font-bold text-slate-900">Credit Usage</h3>
            <p className="text-xs text-slate-400 mt-0.5">Credits consumed last 7 days</p>
          </div>
          {loading ? (
            <div className="h-[200px] bg-slate-50 rounded-xl animate-pulse" />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 5, right: 0, bottom: 0, left: -25 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v} credits`, "Used"]} />
                <Bar dataKey="credits" name="Credits" fill="#0c1e41" radius={[4, 4, 0, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          )}
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400">This week total</p>
              <p className="text-base font-extrabold text-slate-900">
                {chartData.reduce((a, d) => a + (d.credits || 0), 0).toLocaleString()} credits
              </p>
            </div>
            <button onClick={() => navigate("/dashboard/wallet")} className="text-xs text-[#0c1e41] font-semibold flex items-center gap-1 hover:underline">
              History <ArrowRight size={11} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
